// 生成动态路由
import Router from "vue-router";
import getPageTitle from "./get-page-title";
import { queryPreSheetByUserId } from "@/api/preSheet";
import { router, constantRoutes } from "../router";
import { checkRole } from "./index";
import store from "../store";
import { changeCompanyToken } from "@/api/user";
import md5 from "js-md5";
import { MessageBox } from "element-ui";
import { pickCompanyIdentity } from "./pickCompanyIdentity";

let routes = [...constantRoutes];
//定义变量判断是否已经动态添加过，如果刷新后load永远为 0
let load = 0;
// 防止身份切换时的重定向循环
let isSwitchingIdentity = false;
// 正在进行的身份切换 Promise，用于防止并发切换
let switchingIdentityPromise = null;
// 提供一个重置路由的方法
export const resetRouter = () => {
    router.matcher = new Router({
        mode: "history",
        form: process.env.BASE_URL,
        routes,
    });
};

const setRouter = (routes) => {
    router.matcher = new Router({
        mode: "history",
        form: process.env.BASE_URL,
        routes,
    });
};

// 处理预录单路由逻辑
export function preSheetRouter(preSheetRouters) {
    // 如果所有的数据都有了公司全名，那么路由数据更新
    if (preSheetRouters.every((item) => item.company)) {
        // 选出preSheet路由
        const filterRouteIndex = store.state.permission.menuRoutes.findIndex(
            (item) => item.name === "PreSheet",
        );
        // 打造全新路由
        const childrenRoutes = preSheetRouters.map((item) => ({
            path: `preSheet/${item.agentCode}`,
            name: `${item.agentCode}`,
            meta: { title: `${item.shortName}` },
        }));
        const paramRoute = {
            ...store.state.permission.menuRoutes[filterRouteIndex],
            hidden: !preSheetRouters.length,
            children: childrenRoutes,
        };
        const newMenu = [...store.state.permission.menuRoutes];
        newMenu[filterRouteIndex] = paramRoute;
        store.commit("permission/SET_MEAU_ROUTES", newMenu);
    }
}

// 判断是否存在于当前路由列表中
const isExistInCurrentRoutes = (routes, name) => {
    return routes.some((route) => {
        if (route.children) {
            return isExistInCurrentRoutes(route.children, name);
        }
        return route.name === name;
    });
};

/**
 * 生成路由（公共逻辑）
 * @returns {Promise<void>}
 */
const generateRoutesInternal = async () => {
    // 企业体验权限挂在公司 cid 上，必须先拿到企业列表再计算 experiences
    if (store.state.user.token) {
        await store.dispatch("user/updateAllUserRole");
    }
    await store.dispatch("user/updateExperiences");
    const isAdmin = checkRole("admin");
    const accessedRoute = await store.dispatch("permission/generateRoutes", {
        roles: [isAdmin ? "admin" : "staff"],
        experiences: store.state.user.experiences,
        isCompany:
            store.state.user.loginId !== "personal" &&
            store.state.user.cid !== null &&
            store.state.user.cid !== "",
    });
    routes = [...accessedRoute];
    setRouter(routes);
};

/**
 * 生成路由
 * @returns {Promise<void>}
 */
const generateRoutes = async () => {
    await generateRoutesInternal();
};

/**
 * 处理身份切换后的路由更新
 * @returns {Promise<boolean>} - 是否已处理完成
 */
const handleIdentitySwitchAndRoute = async () => {
    isSwitchingIdentity = true;
    try {
        await generateRoutesInternal();
        if (load === 0) {
            load++;
        }
        return true;
    } finally {
        // 注意：这里不重置 isSwitchingIdentity，因为需要在路由守卫中检查
    }
};

/**
 * 路由是否需要按菜单记住身份（强制企业 或 仅记忆身份）
 * @param {Object} routeRecord - 路由记录
 * @returns {boolean}
 */
const isIdentityMenuRoute = (routeRecord) => {
    return (
        !!routeRecord &&
        !!routeRecord.meta &&
        (routeRecord.meta.needCompanyRole === true ||
            routeRecord.meta.rememberIdentity === true)
    );
};

/**
 * 路由是否强制要求企业身份
 * @param {Object} route - 路由对象
 * @returns {boolean}
 */
export const routeRequiresCompanyRole = (route) => {
    if (!route) {
        return false;
    }
    if (route.meta && route.meta.needCompanyRole === true) {
        return true;
    }
    if (!route.matched || route.matched.length === 0) {
        return false;
    }
    return route.matched.some(
        (matchedRoute) =>
            matchedRoute.meta && matchedRoute.meta.needCompanyRole === true,
    );
};

/**
 * 路由是否在「有企业身份时优先使用企业」：当前为个人则切换，多家企业需选择
 * @param {Object} route - 路由对象
 * @returns {boolean}
 */
export const routePrefersCompanyRole = (route) => {
    if (!route) {
        return false;
    }
    if (route.meta && route.meta.preferCompanyRole === true) {
        return true;
    }
    if (!route.matched || route.matched.length === 0) {
        return false;
    }
    return route.matched.some(
        (matchedRoute) =>
            matchedRoute.meta && matchedRoute.meta.preferCompanyRole === true,
    );
};

/**
 * 获取路由的根菜单名称（如果有子菜单，返回父菜单名称）
 * @param {Object} route - 路由对象
 * @returns {string|null} - 根菜单名称
 */
export const getRootMenuName = (route) => {
    if (!route || !route.matched || route.matched.length === 0) {
        return null;
    }

    // 从匹配的路由数组中查找需要记住身份的根菜单
    // 通常根菜单在数组的前面，子菜单在后面
    let identityMenuIndex = -1;
    for (let i = 0; i < route.matched.length; i++) {
        const matchedRoute = route.matched[i];
        if (isIdentityMenuRoute(matchedRoute)) {
            identityMenuIndex = i;
            break;
        }
    }

    // 如果找到了需要记住身份的路由
    if (identityMenuIndex >= 0) {
        // 特殊处理：如果当前路由是 sh_manifest 或 VGM 的子路由，返回 ManifestVgm
        const currentRoutePath = route.matched[identityMenuIndex].path;

        // 青岛舱单有独立体验权限，不跟上海舱单/VGM 共用企业身份
        if (
            currentRoutePath &&
            (currentRoutePath.startsWith("/qd_manifest") ||
                currentRoutePath.startsWith("/qingdao_sft"))
        ) {
            return "qd_manifest";
        }

        // 检查是否是 sh_manifest 或 VGM 的子路由
        if (
            currentRoutePath &&
            (currentRoutePath.startsWith("/sh_manifest") ||
                currentRoutePath.startsWith("/VGM"))
        ) {
            // 查找 ManifestVgm 父路由
            for (let i = identityMenuIndex - 1; i >= 0; i--) {
                const matchedRoute = route.matched[i];
                if (matchedRoute.name === "ManifestVgm") {
                    return "ManifestVgm";
                }
            }
        }

        // 智能舱单：子路由统一记到 SmartManifest
        if (
            currentRoutePath &&
            (currentRoutePath.startsWith("/manifestMake") ||
                currentRoutePath.startsWith("/roroManifest") ||
                currentRoutePath.startsWith("/shManifestRecognize") ||
                currentRoutePath === "smartManifest" ||
                currentRoutePath === "/smartManifest")
        ) {
            for (let i = identityMenuIndex; i >= 0; i--) {
                const matchedRoute = route.matched[i];
                if (matchedRoute.name === "SmartManifest") {
                    return "SmartManifest";
                }
            }
        }

        // 尝试找到它的父路由（有 children 且有 name 的路由作为根菜单）
        // 跳过无 name 的 Layout，避免记住身份 key 丢失
        for (let i = identityMenuIndex - 1; i >= 0; i--) {
            const matchedRoute = route.matched[i];
            if (
                matchedRoute.name &&
                matchedRoute.children &&
                matchedRoute.children.length > 0
            ) {
                return matchedRoute.name;
            }
        }
        // 如果没有找到有 children 的父路由，返回找到的身份菜单路由本身
        return route.matched[identityMenuIndex].name || null;
    }

    return null;
};

/** 智能舱单三个子菜单共用同一企业身份记忆 */
export const getPreferCompanyMenuName = (route) => {
    if (!route || !route.matched) {
        return getRootMenuName(route);
    }
    const preferRoute = route.matched.find(
        (matchedRoute) =>
            matchedRoute.meta && matchedRoute.meta.preferCompanyRole === true,
    );
    if (!preferRoute) {
        return getRootMenuName(route);
    }
    const smartManifest = route.matched.find(
        (matchedRoute) => matchedRoute.name === "SmartManifest",
    );
    if (smartManifest) {
        return "SmartManifest";
    }
    return preferRoute.name || getRootMenuName(route);
};

/**
 * 获取需要企业身份的菜单对应的企业身份key
 * @param {string} menuName - 菜单名称
 */
export const getMenuCompanyIdentityKey = (menuName) => {
    const key = `fcw_web_menuCompanyId_${menuName}_${store.state.user.userId}`;
    return key;
};

/**
 * 保存菜单对应的身份
 * @param {string} menuName - 菜单名称
 * @param {number|"personal"} companyId - 企业ID，或 "personal" 表示个人身份
 */
export const saveMenuCompanyIdentity = (menuName, companyId) => {
    if (!menuName || companyId === undefined || companyId === null || companyId === "") {
        return;
    }
    const key = getMenuCompanyIdentityKey(menuName);
    localStorage.setItem(key, String(companyId));
};

/**
 * 获取菜单对应的身份
 * @param {string} menuName - 菜单名称
 * @returns {number|"personal"|null} - 企业ID、"personal" 或 null
 */
export const getMenuCompanyIdentity = (menuName) => {
    if (!menuName) {
        return null;
    }
    const key = getMenuCompanyIdentityKey(menuName);
    const companyIdStr = localStorage.getItem(key);
    if (!companyIdStr) {
        return null;
    }
    if (companyIdStr === "personal") {
        return "personal";
    }
    return Number(companyIdStr);
};

/**
 * 保存不需要企业身份的路由对应的身份（统一记录一个）
 * @param {number|null} companyId - 企业ID（null表示个人身份）
 */
export const saveNonCompanyRouteIdentity = (companyId) => {
    const key = `fcw_web_nonCompanyRouteId_${store.state.user.userId}`;
    if (companyId) {
        localStorage.setItem(key, String(companyId));
    } else {
        // 如果是个人身份，存储特殊标记
        localStorage.setItem(key, "personal");
    }
};

/**
 * 获取不需要企业身份的路由对应的身份（统一记录）
 * @returns {number|"personal"|null} - 企业ID或"personal"或null
 */
export const getNonCompanyRouteIdentity = () => {
    const key = `fcw_web_nonCompanyRouteId_${store.state.user.userId}`;
    const value = localStorage.getItem(key);
    if (!value) {
        return null;
    }
    if (value === "personal") {
        return "personal";
    }
    return Number(value);
};

/**
 * 确保企业列表已加载
 * @returns {Promise<void>}
 */
const ensureAllUserRoleLoaded = async () => {
    if (
        !store.state.user.allUserRole ||
        store.state.user.allUserRole.length === 0
    ) {
        await store.dispatch("user/updateAllUserRole");
    }
};

/**
 * 根据企业ID查找企业
 * @param {number} companyId - 企业ID
 * @returns {Object|null}
 */
const findCompanyById = (companyId) => {
    const allUserRole = store.state.user.allUserRole || [];
    return allUserRole.find((item) => Number(item.cid) === companyId) || null;
};

/**
 * 检查企业是否有指定的体验权限
 * @param {Object} company - 企业对象
 * @param {Array} requiredExperiences - 需要的体验权限数组
 * @returns {boolean}
 */
const checkCompanyHasExperience = (company, requiredExperiences) => {
    if (
        !requiredExperiences ||
        !Array.isArray(requiredExperiences) ||
        requiredExperiences.length === 0
    ) {
        return true;
    }
    const curUserRoleExperiences = store.getters["user/curUserRoleExperiences"];
    const experiences = curUserRoleExperiences[company.cid] || [];
    return experiences.some((experience) =>
        requiredExperiences.includes(experience),
    );
};

/**
 * 查找有体验权限的目标企业
 * @param {Array} requiredExperiences - 需要的体验权限数组
 * @param {number|null} preferredCompanyId - 优先选择的企业ID
 * @returns {Object|null}
 */
const findTargetCompanyWithExperience = async (
    requiredExperiences,
    preferredCompanyId = null,
) => {
    await ensureAllUserRoleLoaded();
    const allUserRole = store.state.user.allUserRole || [];
    if (allUserRole.length === 0) {
        return null;
    }

    const needExperience =
        requiredExperiences &&
        Array.isArray(requiredExperiences) &&
        requiredExperiences.length > 0;

    // 如果有优先选择的企业ID，先检查该企业
    if (preferredCompanyId) {
        const preferredCompany = findCompanyById(preferredCompanyId);
        if (
            preferredCompany &&
            (!needExperience ||
                checkCompanyHasExperience(
                    preferredCompany,
                    requiredExperiences,
                ))
        ) {
            return preferredCompany;
        }
    }

    // 查找第一个有体验权限的企业
    if (needExperience) {
        const targetCompany = allUserRole.find((company) =>
            checkCompanyHasExperience(company, requiredExperiences),
        );
        return targetCompany || allUserRole[0];
    }

    return allUserRole[0];
};

/**
 * 安全切换身份（防止并发，确保token切换完成）
 * @param {string} targetIdentity - 目标身份
 * @returns {Promise<boolean>} - 切换是否成功
 */
const switchIdentitySafely = async (targetIdentity) => {
    if (switchingIdentityPromise) {
        await switchingIdentityPromise;
    }

    // 启动身份切换（会等待token切换完成）
    switchingIdentityPromise = switchIdentity(targetIdentity);

    try {
        // 等待身份切换完成（包括token切换）
        const switchSuccess = await switchingIdentityPromise;

        // 路由更新异步执行，不阻塞返回
        if (switchSuccess) {
            handleIdentitySwitchAndRoute().catch((err) => {
                console.error("路由更新失败:", err);
            });
        }

        switchingIdentityPromise = null;
        return switchSuccess;
    } catch (error) {
        // token获取失败，显示弹窗提示
        console.error("身份切换失败:", error);
        switchingIdentityPromise = null;

        // 使用 ElementUI 的 MessageBox 显示提示
        MessageBox.alert("自动切换身份失败，请手动切换", "提示", {
            confirmButtonText: "我知道了",
            type: "warning",
        });

        return false;
    }
};

/**
 * 检查当前身份是否匹配目标企业
 * @param {Object} targetCompany - 目标企业对象
 * @returns {boolean}
 */
const isCurrentIdentityMatch = (targetCompany) => {
    return (
        store.state.user.loginId === targetCompany.cname &&
        Number(store.state.user.cid) === Number(targetCompany.cid)
    );
};

/**
 * 当前路由需要的体验权限。子路由 meta 优先，没有则沿 matched 向上找父级。
 * @param {Object} route
 * @returns {Array}
 */
const getRequiredExperiences = (route) => {
    if (!route) {
        return [];
    }
    const matched = route.matched || [];
    for (let i = matched.length - 1; i >= 0; i--) {
        const experiences = matched[i].meta && matched[i].meta.experiences;
        if (Array.isArray(experiences) && experiences.length > 0) {
            return experiences;
        }
    }
    const direct = route.meta && route.meta.experiences;
    if (Array.isArray(direct) && direct.length > 0) {
        return direct;
    }
    return [];
};

/**
 * 处理需要企业身份但当前是个人身份的情况
 * @param {Object} to - 目标路由对象
 * @returns {Promise<boolean>} - 是否切换成功
 */
const handleNeedCompanyButPersonal = async (to) => {
    const requiredExperiences = getRequiredExperiences(to);

    // 优先使用菜单记录的身份
    const rootMenuName = getRootMenuName(to);
    let preferredCompanyId = null;

    if (rootMenuName) {
        const menuCompanyId = getMenuCompanyIdentity(rootMenuName);
        if (typeof menuCompanyId === "number") {
            preferredCompanyId = menuCompanyId;
        }
    }

    const targetCompany = await findTargetCompanyWithExperience(
        requiredExperiences,
        preferredCompanyId,
    );

    if (!targetCompany) {
        console.warn("未找到企业身份");
        return false;
    }

    if (!isCurrentIdentityMatch(targetCompany)) {
        return await switchIdentitySafely(targetCompany.cname);
    }

    return true;
};

/**
 * 处理需要企业身份且当前已经是企业身份的情况
 * @param {Object} to - 目标路由对象
 * @returns {Promise<boolean>} - 是否切换成功
 */
const handleNeedCompanyAndIsCompany = async (to) => {
    const requiredExperiences = getRequiredExperiences(to);
    const needExperience =
        requiredExperiences &&
        Array.isArray(requiredExperiences) &&
        requiredExperiences.length > 0;

    // 优先使用菜单记录的身份
    const rootMenuName = getRootMenuName(to);
    let preferredCompanyId = null;

    if (rootMenuName) {
        const menuCompanyId = getMenuCompanyIdentity(rootMenuName);
        if (typeof menuCompanyId === "number") {
            preferredCompanyId = menuCompanyId;
        }
    }

    // 如果不需要体验资格，使用菜单记录的企业
    if (!needExperience) {
        if (preferredCompanyId) {
            await ensureAllUserRoleLoaded();
            const preferredCompany = findCompanyById(preferredCompanyId);
            if (preferredCompany && !isCurrentIdentityMatch(preferredCompany)) {
                return await switchIdentitySafely(preferredCompany.cname);
            }
        }
        return true;
    }

    // 需要体验资格，检查当前企业是否有体验资格
    await ensureAllUserRoleLoaded();
    await store.dispatch("user/updateExperiences");

    const currentCompany = findCompanyById(Number(store.state.user.cid));
    if (!currentCompany) {
        return false;
    }

    const currentCompanyHasExperience = checkCompanyHasExperience(
        currentCompany,
        requiredExperiences,
    );

    // 如果当前企业没有体验资格，切换到有体验资格的企业
    if (!currentCompanyHasExperience) {
        const currentCompanyCid = Number(store.state.user.cid);
        // 优先使用菜单记录的企业，但要确保有体验资格
        const targetPreferredCompanyId =
            preferredCompanyId && preferredCompanyId !== currentCompanyCid
                ? preferredCompanyId
                : null;
        const targetCompany = await findTargetCompanyWithExperience(
            requiredExperiences,
            targetPreferredCompanyId,
        );

        if (targetCompany && Number(targetCompany.cid) !== currentCompanyCid) {
            return await switchIdentitySafely(targetCompany.cname);
        }
    } else if (
        preferredCompanyId &&
        Number(store.state.user.cid) !== preferredCompanyId
    ) {
        // 如果当前企业有体验资格，但菜单记录的是其他企业，检查记录的企业是否也有体验资格
        const preferredCompany = findCompanyById(preferredCompanyId);
        if (
            preferredCompany &&
            checkCompanyHasExperience(preferredCompany, requiredExperiences)
        ) {
            // 记录的企业也有体验资格，切换到记录的企业
            return await switchIdentitySafely(preferredCompany.cname);
        }
    }

    return true;
};

/**
 * 处理按菜单记住身份（允许个人，不强制企业）
 * @param {Object} to - 目标路由对象
 * @returns {Promise<boolean>} - 是否切换成功
 */
const handleRememberMenuIdentity = async (to) => {
    const rootMenuName = getRootMenuName(to);
    if (!rootMenuName) {
        return true;
    }

    const savedIdentity = getMenuCompanyIdentity(rootMenuName);
    if (!savedIdentity) {
        // 首次进入：把当前身份记到该菜单，避免离开后再回来丢失
        const isPersonalIdentity =
            store.state.user.loginId === "personal" ||
            store.state.user.cid === null;
        if (isPersonalIdentity) {
            saveMenuCompanyIdentity(rootMenuName, "personal");
        } else if (store.state.user.cid) {
            saveMenuCompanyIdentity(rootMenuName, store.state.user.cid);
        }
        return true;
    }

    if (savedIdentity === "personal") {
        const isPersonalIdentity =
            store.state.user.loginId === "personal" ||
            store.state.user.cid === null;
        if (!isPersonalIdentity) {
            return await switchIdentitySafely("personal");
        }
        return true;
    }

    await ensureAllUserRoleLoaded();
    const preferredCompany = findCompanyById(savedIdentity);
    if (preferredCompany && !isCurrentIdentityMatch(preferredCompany)) {
        return await switchIdentitySafely(preferredCompany.cname);
    }

    return true;
};

let ensuringPreferCompanyPromise = null;

/**
 * 有企业身份且当前是个人时切到企业；多家企业弹出选择
 * @param {Object} to - 目标路由
 * @returns {Promise<boolean>}
 */
export const ensurePreferCompanyIdentity = async (to) => {
    if (ensuringPreferCompanyPromise) {
        return ensuringPreferCompanyPromise;
    }
    ensuringPreferCompanyPromise = handlePreferCompanyIfAvailable(to);
    try {
        return await ensuringPreferCompanyPromise;
    } finally {
        ensuringPreferCompanyPromise = null;
    }
};

/**
 * 处理「有企业则用企业身份」：无企业保持个人；一家自动切换；多家需选择
 * @param {Object} to - 目标路由对象
 * @returns {Promise<boolean>}
 */
const handlePreferCompanyIfAvailable = async (to) => {
    const isPersonalIdentity =
        store.state.user.loginId === "personal" ||
        store.state.user.cid === null;

    await ensureAllUserRoleLoaded();
    const companies = store.state.user.allUserRole || [];
    if (!companies.length) {
        return true;
    }

    const preferMenuName = getPreferCompanyMenuName(to);
    const savedIdentity = preferMenuName
        ? getMenuCompanyIdentity(preferMenuName)
        : null;
    const savedCompany =
        typeof savedIdentity === "number"
            ? findCompanyById(savedIdentity)
            : null;

    if (!isPersonalIdentity) {
        if (savedCompany && !isCurrentIdentityMatch(savedCompany)) {
            return await switchIdentitySafely(savedCompany.cname);
        }
        if (preferMenuName && store.state.user.cid && !savedCompany) {
            saveMenuCompanyIdentity(preferMenuName, store.state.user.cid);
        }
        return true;
    }

    let targetCompany = savedCompany;
    if (!targetCompany && companies.length === 1) {
        targetCompany = companies[0];
    }
    if (!targetCompany) {
        targetCompany = await pickCompanyIdentity(companies);
        if (!targetCompany) {
            return false;
        }
    }

    if (preferMenuName && targetCompany.cid) {
        saveMenuCompanyIdentity(preferMenuName, targetCompany.cid);
    }

    if (!isCurrentIdentityMatch(targetCompany)) {
        return await switchIdentitySafely(targetCompany.cname);
    }
    return true;
};

/**
 * 处理不需要企业身份的情况
 * @param {Object} to - 目标路由对象
 * @returns {Promise<boolean>} - 是否切换成功
 */
const handleNotNeedCompany = async (to) => {
    // 使用不需要企业身份的路由统一记录的身份
    let preferredIdentity = null;
    const routeIdentity = getNonCompanyRouteIdentity();
    if (routeIdentity) {
        if (routeIdentity === "personal") {
            preferredIdentity = "personal";
        } else {
            // 是企业ID，需要验证企业是否还存在
            await ensureAllUserRoleLoaded();
            const routeCompany = findCompanyById(routeIdentity);
            if (routeCompany) {
                preferredIdentity = routeCompany.cname;
            }
        }
    }

    // 根据优先身份进行切换
    if (preferredIdentity) {
        if (preferredIdentity === "personal") {
            const isPersonalIdentity =
                store.state.user.loginId === "personal" ||
                store.state.user.cid === null;
            if (!isPersonalIdentity) {
                return await switchIdentitySafely("personal");
            }
        } else {
            // 是企业身份
            await ensureAllUserRoleLoaded();
            const preferredCompany = store.state.user.allUserRole.find(
                (item) => item.cname === preferredIdentity,
            );
            if (preferredCompany && !isCurrentIdentityMatch(preferredCompany)) {
                return await switchIdentitySafely(preferredIdentity);
            }
        }
    } else {
        // 如果没有记录，且当前是企业身份，切换回个人身份
        const isCompanyIdentity =
            store.state.user.loginId !== "personal" &&
            store.state.user.cid !== null;
        if (isCompanyIdentity) {
            return await switchIdentitySafely("personal");
        }
    }

    return true;
};

/**
 * 更新token并保存到store和localStorage
 * @param {string} token - token值
 */
const updateToken = (token) => {
    if (token) {
        localStorage.setItem("token", token);
        store.commit("user/editToken", token);
    }
};

// token获取的Promise缓存，避免重复请求
let tokenFetchPromise = null;
let currentFetchingCompanyId = null;

/**
 * 从缓存获取token
 * @param {string} companyId - 企业ID，空字符串表示个人身份
 * @returns {string|null} 缓存的token
 */
export const getCachedToken = (companyId) => {
    const identityKey = companyId;
    const key = `fcw_web_token_${store.state.user.userId}_${identityKey}`;
    return localStorage.getItem(key);
};

/**
 * 清除缓存token
 */

/**
 * 缓存token
 * @param {string} companyId - 企业ID，空字符串表示个人身份
 * @param {string} token - token值
 */
export const cacheToken = (companyId, token) => {
    if (!token) {
        return;
    }
    const identityKey = companyId || "personal";
    const key = `fcw_web_token_${store.state.user.userId}_${identityKey}`;
    localStorage.setItem(key, token);
};

/**
 * 异步获取并更新token（只检查缓存token）
 * @param {string} companyId - 企业ID，空字符串表示个人身份
 * @returns {Promise<string|null>} - 返回token
 */
const fetchTokenAsync = async (companyId) => {
    // 先检查缓存中是否有该身份的token
    const cachedToken = getCachedToken(companyId);
    if (cachedToken) {
        // 如果缓存中有token，直接使用，不阻塞
        updateToken(cachedToken);
        // 在后台静默更新token，确保token是最新的
        // changeCompanyToken({ companyId })
        //     .then(res => {
        //         const token = res.data.data;
        //         if (token) {
        //             cacheToken(companyId, token);
        //             updateToken(token);
        //         }
        //     })
        //     .catch(err => {
        //         console.warn('后台token更新失败:', err);
        //         // 后台更新失败不影响，继续使用缓存的token
        //     });
        // 立即返回，不阻塞
        return Promise.resolve(cachedToken);
    }

    // 如果正在获取相同的token，直接返回现有的Promise
    if (tokenFetchPromise && currentFetchingCompanyId === companyId) {
        return tokenFetchPromise;
    }

    // 如果没有缓存token，需要等待获取新token（阻塞）
    currentFetchingCompanyId = companyId;
    tokenFetchPromise = (async () => {
        try {
            const res = await changeCompanyToken({ companyId });
            const token = res.data.data;
            if (token) {
                // 缓存token
                cacheToken(companyId, token);
                updateToken(token);
                return token;
            } else {
                // token为空，抛出错误
                throw new Error("获取到的token为空");
            }
        } catch (error) {
            console.error("获取token失败:", error);
            // token获取失败，抛出错误，让上层处理
            throw error;
        } finally {
            // 清除缓存
            tokenFetchPromise = null;
            currentFetchingCompanyId = null;
        }
    })();

    return tokenFetchPromise;
};

/**
 * 切换用户身份（确保token切换完成后再返回）
 * @param {string} targetIdentity - 目标身份: 'personal' 或个人身份名称
 * @returns {Promise<boolean>} - 切换是否成功
 */
const switchIdentity = async (targetIdentity) => {
    try {
        // 如果目标身份是个人身份
        if (targetIdentity === "personal") {
            const companyId = "";

            // 先获取token（等待完成），确保token已经更新
            await fetchTokenAsync(companyId);

            // 更新状态
            const localtype = md5("role");
            const role = md5("" + "/" + store.state.user.userId);
            localStorage.setItem(localtype, role);
            store.commit("user/editCid", null);
            store.commit("user/editLoginId", "personal");
            localStorage.setItem("loginId", "personal");

            return true;
        }

        // 切换到企业身份
        // 检查企业列表是否已加载
        const hasAllUserRole =
            store.state.user.allUserRole &&
            store.state.user.allUserRole.length > 0;

        if (hasAllUserRole) {
            // 如果企业列表已加载，立即查找并切换
            const targetCompany = store.state.user.allUserRole.find(
                (item) => item.cname === targetIdentity,
            );

            if (!targetCompany) {
                console.warn("未找到目标企业身份");
                return false;
            }

            const cid = targetCompany.cid || "";

            // 先获取token（等待完成），确保token已经更新
            await fetchTokenAsync(cid);

            // 更新状态
            const localtype = md5("role");
            const role = md5(
                (targetCompany.role || "") + "/" + store.state.user.userId,
            );
            localStorage.setItem(localtype, role);
            store.commit("user/editCid", cid);
            store.commit("user/editLoginId", targetIdentity);
            localStorage.setItem("loginId", targetIdentity);

            return true;
        } else {
            // 如果企业列表未加载，需要先加载（这个会阻塞，但只在首次切换时发生）
            await ensureAllUserRoleLoaded();

            // 查找目标企业
            const targetCompany = store.state.user.allUserRole.find(
                (item) => item.cname === targetIdentity,
            );

            if (!targetCompany) {
                console.warn("未找到目标企业身份");
                return false;
            }

            const cid = targetCompany.cid || "";

            // 先获取token（等待完成），确保token已经更新
            await fetchTokenAsync(cid);

            // 更新状态
            const localtype = md5("role");
            const role = md5(
                (targetCompany.role || "") + "/" + store.state.user.userId,
            );
            localStorage.setItem(localtype, role);
            store.commit("user/editCid", cid);
            store.commit("user/editLoginId", targetIdentity);
            localStorage.setItem("loginId", targetIdentity);

            return true;
        }
    } catch (error) {
        MessageBox.alert("切换身份失败，请手动切换", "提示", {
            confirmButtonText: "我知道了",
            type: "warning",
        });
        return false;
    }
};

//导航守卫
router.beforeEach(async (to, from, next) => {
    // 设置页面标题
    document.title = getPageTitle(to.meta.title);

    // 如果正在切换身份，等待切换完成
    if (switchingIdentityPromise) {
        await switchingIdentityPromise;
        switchingIdentityPromise = null;
    }

    // 如果正在切换身份，跳过身份检查，直接继续执行后续逻辑
    if (isSwitchingIdentity) {
        isSwitchingIdentity = false;
        // 继续执行后续的路由处理逻辑，不在这里return
    }

    // 检查路由是否需要企业身份（在登录状态下）
    const rootMenuName = getRootMenuName(to);
    const needCompanyRole = routeRequiresCompanyRole(to);
    const preferCompanyRole = routePrefersCompanyRole(to);
    const hasRememberIdentity = !!rootMenuName && !needCompanyRole;
    const isCompanyIdentity =
        store.state.user.loginId !== "personal" &&
        store.state.user.cid !== null;
    const isPersonalIdentity =
        store.state.user.loginId === "personal" ||
        store.state.user.cid === null;

    // 如果 from 和 to 是同一路由，或者路径为根路径（刷新操作），不进行自动身份切换
    const isSameRoute =
        (from.path === to.path && from.name === to.name) ||
        from.fullPath === to.fullPath ||
        from.path === "/";

    // 在登录状态下处理身份切换逻辑
    if (!isSameRoute && store.state.user.token) {
        let switchSuccess = true;
        if (needCompanyRole && isPersonalIdentity) {
            // 需要企业身份但当前是个人身份
            switchSuccess = await handleNeedCompanyButPersonal(to);
        } else if (needCompanyRole && isCompanyIdentity) {
            // 需要企业身份且当前已经是企业身份
            switchSuccess = await handleNeedCompanyAndIsCompany(to);
        } else if (preferCompanyRole) {
            // 有企业身份则切企业；当前是个人且有多家企业时需选择
            switchSuccess = await ensurePreferCompanyIdentity(to);
        } else if (hasRememberIdentity) {
            // 按菜单记住身份（个人/企业均可）
            switchSuccess = await handleRememberMenuIdentity(to);
        } else if (!needCompanyRole) {
            // 不需要企业身份
            switchSuccess = await handleNotNeedCompany(to);
        }

        // 如果身份切换失败，阻止路由跳转
        if (!switchSuccess) {
            next(false);
            return;
        }
    }

    //非登录、有菜单数据、 没有进行添加（或者刷新了）
    if (load === 0) {
        load++;
        //再次调用存储菜单数据(前提是在存储的地方有调用添加路由规则)、或者直接调用动态添加路由规则事件
        // 生成路由，根据权限
        // 处理预录单路由(登录有即可展示)
        // store.state.user.token && handlePreSheetRouter();
        // 生成异步路由(目前只有admin和staff权限)
        await generateRoutes();
        if (isExistInCurrentRoutes(routes, to.name)) {
            next({ ...to, replace: true });
        } else {
            next({ path: "/404", replace: true });
        }
    } else {
        // 物流实时查询模块符合条件无需登录也可进入，而其他模块需要登录，这里处理从物流实时查询跳转其他页面逻辑
        !store.state.user.token &&
            setTimeout(() => {
                if (from.fullPath.includes("boxquery")) {
                    loginDialog();
                }
            }, 500);
        next();
        // 预录单模块无需登录也可进入，而其他模块需要登录，这里处理从预录单跳转其他页面逻辑
        !store.state.user.token &&
            setTimeout(() => {
                if (
                    from.fullPath.includes("preSheet") &&
                    to.fullPath !== "401"
                ) {
                    loginDialog();
                }
            }, 500);
    }
});

// 处理预录单路由
function handlePreSheetRouter() {
    queryPreSheetByUserId().then(({ data }) => {
        if (data.data && data.data.constructor == Array) {
            preSheetRouter(data.data);
        }
    });
}

export default {
    install(Vue) {
        Vue.prototype.checkPer = (value) => {
            if (value && value instanceof Array && value.length > 0) {
                const roles = store.state.user && store.state.user.roles;
                const permissionRoles = value;
                if (roles) {
                    // console.log('roles==',roles);

                    return roles.some((role) => {
                        return permissionRoles.includes(role);
                    });
                } else {
                    return false;
                }
            } else {
                console.error(
                    `need roles! Like v-permission="['admin','editor']"`,
                );
                return false;
            }
        };
    },
};
